import { useCallback } from 'react';

import { scrollTo } from '../../shared/helpers';

type UseCarouselNavigationProps = {
  carousel: React.RefObject<HTMLDivElement>;
  id: string;
  firstItemIsVisible: boolean;
  lastItemIsVisible: boolean;
  firstVisibleIndex: number;
  lastVisibleIndex: number;
  infiniteScroll: boolean;
  childVisibilityLength: number;
};

type UseCarouselNavigationReturn = {
  next: (e: React.MouseEvent<HTMLButtonElement>) => void;
  back: (e: React.MouseEvent<HTMLButtonElement>) => void;
  goTo: (e: React.MouseEvent<HTMLButtonElement>, i: number) => void;
};

/**
 * Hook for carousel navigation logic (next, back, goTo)
 */
export const useCarouselNavigation = ({
  carousel,
  id,
  firstItemIsVisible,
  lastItemIsVisible,
  firstVisibleIndex,
  lastVisibleIndex,
  infiniteScroll,
  childVisibilityLength,
}: UseCarouselNavigationProps): UseCarouselNavigationReturn => {
  const getStepEl = useCallback(
    (index: number): Element | null => {
      if (carousel.current) {
        // Use data attributes with values to avoid CSS selector issues with special characters
        const selector = `[data-carousel-id="${id}"][data-carousel-index="${index}"]`;
        return carousel.current.querySelector(selector);
      }
      return null;
    },
    [carousel, id]
  );

  const next = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (lastItemIsVisible && !infiniteScroll) return;

      const scrollToIndex =
        infiniteScroll && lastVisibleIndex === childVisibilityLength - 1
          ? 0
          : lastVisibleIndex + 1;
      const el = getStepEl(scrollToIndex);
      if (el) {
        scrollTo(
          el as HTMLElement,
          {
            behavior: 'smooth',
            inline: 'start',
            block: 'nearest',
          },
          carousel.current
        );
      }
    },
    [
      lastItemIsVisible,
      lastVisibleIndex,
      infiniteScroll,
      childVisibilityLength,
      getStepEl,
      carousel,
    ]
  );

  const back = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (firstItemIsVisible && !infiniteScroll) return;

      const scrollToIndex =
        firstVisibleIndex === 0 && infiniteScroll
          ? childVisibilityLength - 1
          : firstVisibleIndex - 1;
      const el = getStepEl(scrollToIndex);
      if (el) {
        scrollTo(
          el as HTMLElement,
          {
            behavior: 'smooth',
            inline: 'end',
            block: 'nearest',
          },
          carousel.current
        );
      }
    },
    [
      firstItemIsVisible,
      firstVisibleIndex,
      infiniteScroll,
      childVisibilityLength,
      getStepEl,
      carousel,
    ]
  );

  const goTo = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, i: number) => {
      e.preventDefault();
      const el = getStepEl(i);

      if (el) {
        scrollTo(el as HTMLElement, {
          behavior: 'smooth',
          inline: 'center',
          block: 'center',
        });
      }
    },
    [getStepEl]
  );

  return { next, back, goTo };
};
